import { useCallback, useEffect, useRef, useState } from "react";
import { Swipeable } from "react-swipeable";
import {
  type CallToAction,
  type DynamicOfferContent,
  useDynamicOffers,
} from "@apac/feature-flags";
import { useModuleBEM } from "@b2bportal/core-themes";
import { trackEvent } from "@hopper-b2b/api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { PremierDestinationEarnDialog } from "@hsbc/portal";
import { FeatureFlagNames } from "@hsbc/utilities";
import { useCarousel } from "@tenant/ui";
import {
  usePremierDestinationPromotion,
  type AccountPromotions,
} from "../../promotions";
import { OfferDialog } from "../OfferDialog";
import styles from "./styles.module.scss";

const AUTOPLAY_INTERVAL = 3000;
const PREMIER_DESTINATION_DIALOG_BANNERS = [
  "hsbc-offers-premier-destination-banner",
  "hsbc-offers-launch-accelerate-earn",
  "hsbc-offers-launch-accelerate-earn-non-premier",
];

export interface HsbcOffer extends Omit<DynamicOfferContent, "image"> {
  provider?: "hsbc" | "hopper" | "joint";
  accountPromotion?: AccountPromotions;
  styles?: {
    position: "left" | "right";
    textColor: "black" | "white";
  };
  image: {
    url: string;
    position: "background" | "left" | "right";
  };
  description?: {
    title: string;
    body?: string;
    terms?: string;
    image?: string;
    callToAction?: Array<CallToAction>;
  };
}

export const HomepageBanner = () => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "HomepageBanner");

  const { isPromotionEnabled } = usePremierDestinationPromotion();
  const { sortedOffers } = useDynamicOffers<HsbcOffer>({
    flagPrefix: FeatureFlagNames.OFFERS,
  });

  const offers = sortedOffers.filter(({ accountPromotion }) =>
    // If accountPromotion is null, it means the offer is not associated with any promotion
    accountPromotion == null ? true : isPromotionEnabled(accountPromotion)
  );

  const { context, handlers } = useCarousel({
    numberOfItemsToDisplay: 1,
    items: offers,
  });

  const [isAutoPlay, setAutoPlay] = useState(true);
  const stopAutoPlay = useCallback(() => setAutoPlay(false), []);
  const startAutoPlay = useCallback(() => setAutoPlay(true), []);
  const loggedIndices = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!isAutoPlay) return;
    const id = setInterval(() => handlers.onNextClick(), AUTOPLAY_INTERVAL);
    return () => clearInterval(id);
  }, [handlers, isAutoPlay]);

  const onBannerClick = () => {
    const { currentIndex } = context;
    const { name } = offers?.[currentIndex] ?? {};

    if (name != null) {
      trackEvent({
        eventName: "tapped_banner",
        properties: {
          banner_name: name,
          screen: "homepage",
          position: currentIndex,
        },
      });
    }

    stopAutoPlay();
  };

  useEffect(() => {
    const { currentIndex } = context;
    const { name } = offers?.[currentIndex] ?? {};

    // Only track if we haven't logged this index before
    if (!loggedIndices.current.has(currentIndex) && name != null) {
      trackEvent({
        eventName: "viewed_banner",
        properties: {
          banner_name: name,
          screen: "homepage",
          position: currentIndex,
        },
      });
      loggedIndices.current.add(currentIndex);
    }
  }, [context, offers]);

  return (
    <Swipeable
      className={block}
      onSwipedLeft={handlers.onNextClick}
      onSwipedRight={handlers.onPreviousClick}
      preventDefaultTouchmoveEvent
    >
      <div ref={context.containerRef} className={cn("scrollable")}>
        {offers.map((item) => {
          // Note: This is a hack to show the Premier Destination Earn Dialog on the homepage banner
          // This is not a good practice and only because of definite need to show a custom dialog on the homepage banner
          if (PREMIER_DESTINATION_DIALOG_BANNERS.includes(item.name ?? "")) {
            return (
              <OfferDialog
                {...item}
                key={item.key}
                onOpen={onBannerClick}
                onClose={startAutoPlay}
                description={{
                  title: t("banners.premierDestination.dialog.title"),
                }}
              >
                <PremierDestinationEarnDialog />
              </OfferDialog>
            );
          } else {
            return (
              <OfferDialog
                {...item}
                key={item.key}
                onOpen={onBannerClick}
                onClose={startAutoPlay}
              />
            );
          }
        })}
      </div>
    </Swipeable>
  );
};
